import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const LOOKUPS_BASE = 'Lookups/';
const DEFAULT_PAGE_SIZE = 20;

/** API response: ApiResponse<DataItemsResult<T>> (backend returns camelCase) */
interface LookupApiResponse {
  data?: {
    items?: unknown[];
    totalItems?: number;
    pageNumber?: number;
    itemsPerPage?: number;
    pagesCount?: number;
  };
  succeeded?: boolean;
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class LookupDataService {
  private readonly baseUrl = environment.apiBaseUrl + LOOKUPS_BASE;

  constructor(private http: HttpClient) {}

  private buildParams(options: LookupOptions): HttpParams {
    let params = new HttpParams()
      .set('pageNumber', String(options.pageNumber ?? 1))
      .set('pageSize', String(options.pageSize ?? DEFAULT_PAGE_SIZE));

    if (options.searchTerm?.trim()) {
      params = params.set('searchTerm', options.searchTerm.trim());
    }
    // Backend rejects empty string in SelectedIds; only send non-empty valid ids
    const selectedIds = (options.selectedIds ?? []).filter(
      id => id != null && String(id).trim() !== ''
    );
    selectedIds.forEach(id => {
      params = params.append('selectedIds', String(id));
    });
    return params;
  }

  private getLookup<T>(endpoint: string, options: LookupOptions): Observable<LookupResult<T>> {
    const opts: LookupOptions = {
      pageNumber: options?.pageNumber ?? 1,
      pageSize: options?.pageSize ?? DEFAULT_PAGE_SIZE,
      searchTerm: options?.searchTerm,
      selectedIds: options?.selectedIds ?? []
    };
    const params = this.buildParams(opts);

    return this.http.get<LookupApiResponse>(this.baseUrl + endpoint, { params }).pipe(
      map(res => {
        const payload = res?.data;
        const items = payload?.items ?? [];
        const totalItems = payload?.totalItems ?? 0;
        const pageNumber = payload?.pageNumber ?? opts.pageNumber!;
        const pagesCount = payload?.pagesCount ?? 0;
        const hasMore = pagesCount > 0 ? pageNumber < pagesCount : (pageNumber * (opts.pageSize!)) < totalItems;
        return { items: items as T[], totalCount: totalItems, hasMore };
      }),
      catchError(() => of({ items: [], totalCount: 0, hasMore: false }))
    );
  }

  private defaultOptions(): LookupOptions {
    return { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE };
  }

  getCities(options?: LookupOptions): Observable<LookupResult<City>> {
    return this.getLookup<City>('cities', options ?? this.defaultOptions());
  }

  getColleges(options?: LookupOptions): Observable<LookupResult<College>> {
    return this.getLookup<College>('colleges', options ?? this.defaultOptions());
  }

  getMajors(options?: LookupOptions): Observable<LookupResult<Major>> {
    return this.getLookup<Major>('majors', options ?? this.defaultOptions());
  }

  getProgramTypes(options?: LookupOptions): Observable<LookupResult<ProgramType>> {
    return this.getLookup<ProgramType>('program-types', options ?? this.defaultOptions());
  }

  getGpaSystems(options?: LookupOptions): Observable<LookupResult<GpaSystem>> {
    return this.getLookup<GpaSystem>('gpa-systems', options ?? this.defaultOptions());
  }

  getDegreeTypes(options?: LookupOptions): Observable<LookupResult<DegreeType>> {
    return this.getLookup<DegreeType>('degree-types', options ?? this.defaultOptions());
  }

  getOpportunityCategories(options?: LookupOptions): Observable<LookupResult<OpportunityCategory>> {
    return this.getLookup<OpportunityCategory>('opportunity-category-types', options ?? this.defaultOptions());
  }

  getExperienceTypes(options?: LookupOptions): Observable<LookupResult<ExperienceType>> {
    return this.getLookup<ExperienceType>('experience-types', options ?? this.defaultOptions());
  }

  getStudentStatuses(options?: LookupOptions): Observable<LookupResult<StudentStatus>> {
    return this.getLookup<StudentStatus>('student-statuses', options ?? this.defaultOptions());
  }

  getLanguages(options?: LookupOptions): Observable<LookupResult<Language>> {
    return this.getLookup<Language>('languages', options ?? this.defaultOptions());
  }

  getLanguageProficiencies(options?: LookupOptions): Observable<LookupResult<LanguageProficiency>> {
    return this.getLookup<LanguageProficiency>('language-proficiencies', options ?? this.defaultOptions());
  }

  getInterests(options?: LookupOptions): Observable<LookupResult<Interest>> {
    return this.getLookup<Interest>('interests', options ?? this.defaultOptions());
  }

  getSkills(options?: LookupOptions): Observable<LookupResult<Skill>> {
    return this.getLookup<Skill>('skills', options ?? this.defaultOptions());
  }
}
